import { useCanvasStore } from "@/stores/canvas-store";
import { agentCursorAnnotationTargets } from "./agent-annotation";

/** What an agent step moved: one display's content after a foreground
 * scroll, or everything the agent running one execution marked - earlier
 * turns included - after a page key, a click that changed the page, or a
 * direct (browser page or background) scroll, none of which names a display. */
export type MovedScope = { displayId: number } | { executionId: string };

/** Screen-space marks no longer describe the view once the agent moved it.
 * Only marks drawn before the move (`since`, wall-clock ms) are stale: one a
 * later step drew stays. Keep tombstones so progressive updates cannot replay
 * the old marks. Preserve the active pointer/narration: completion still needs
 * their paint receipt. */
export function invalidateMovedAnnotations(
  scope: MovedScope,
  since = Number.POSITIVE_INFINITY,
): void {
  useCanvasStore.setState((state) => {
    const agentCursors = new Map(state.agentCursors);
    let changed = false;
    for (const [id, cursor] of agentCursors) {
      const targets = agentCursorAnnotationTargets(cursor);
      const moved = (target: { displayId: number; executionId?: string }) =>
        "displayId" in scope
          ? target.displayId === scope.displayId
          : cursor.executionId === scope.executionId ||
            target.executionId === scope.executionId;
      const stale = (target: (typeof targets)[number]) =>
        moved(target) && (target.drawnAt ?? 0) < since;
      // Exclusions belong to the live mark: they stay while it is newer.
      const live = cursor.annotationTarget;
      const exclusionsStale = !live || (live.drawnAt ?? 0) < since;
      const exclusions = cursor.annotationExclusionTargets;
      if (
        ![...targets, ...(live ? [live] : [])].some(
          (target) => stale(target) && !target.dismissed,
        ) &&
        !(exclusionsStale && exclusions?.some(moved))
      )
        continue;
      const dismiss = (target: (typeof targets)[number]) =>
        stale(target) ? { ...target, dismissed: true } : target;
      changed = true;
      agentCursors.set(id, {
        ...cursor,
        annotationTargets: targets.map(dismiss),
        annotationTarget: live && dismiss(live),
        annotationExclusionTargets: exclusionsStale
          ? exclusions?.filter((target) => !moved(target))
          : exclusions,
      });
    }
    return changed ? { agentCursors } : state;
  });
}
